import { OdsVendorCard } from '@components/OdsVendorCard/OdsVendorCard';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import useIsVisible from '@hooks/useIsVisible';
import { useTrackVendorImpression } from '@hooks/useTrackVendorImpression';
import { getCategory } from '@redux/category';
import {
	ADDITIONAL_CITY_VENDOR_LIMIT,
	getRecommendedVendorProfilesByCity,
	getRecommendedVendorTotalByCity,
	isRecommendationPermittedByCity,
} from '@redux/statePage';
import { getStateCode } from '@redux/states';
import { TextEditorial } from '@tkww/orion-web-ds';
import classNames from 'classnames';
import startCase from 'lodash/startCase';
import React, { type FunctionComponent } from 'react';
import { connect } from 'react-redux';

import { renderViewAllText } from '../../../../utils/text';
import {
	BANDED_DISTANCE,
	CLICK_THROUGH_TO_VENDORS,
	CLICK_THROUGH_TO_VENDOR_CATEGORY,
	RECOMMENDED_VENDORS,
	STATE_LANDING_PAGE,
	widgetViewed,
} from '../../../../utils/tracking';
import urlSafe, {
	INTERNAL_REFERRER_SORT_PARAM,
} from '../../../../utils/urlHelpers';

import TrackableContainer from '../../../../components/shared/TrackableContainer';
import ViewAllButton from '../../../../components/shared/ViewAllButton';

import OrionVendorCardProps from '@components/card/helpers/vendor_card_props/OrionVendorCardProps';
import { useIsDirectoryPageMinPeakPriceEnabled } from '@hooks/use-directory-page-min-peak-price';
import { GridColumn, GridRow } from '@tkww/orion-web-ds-structure';
import type { ImpressionType } from '@typings/vendorImpressions';
import ClientStyles from '../../../../styles.scss';
import StateStyles from '../../styles.scss';
import Styles from './styles.scss';

export interface StateProps {
	category: Category.Category;
	isPermitted: boolean;
	stateCode: string;
	total: number;
	vendors: Vendor.Raw[];
}

export interface OwnProps {
	city: string;
}

export type Props = OwnProps & StateProps;

const sourceInfo = {
	sourcePage: STATE_LANDING_PAGE,
	sourceContent: RECOMMENDED_VENDORS,
	sourceFormula: BANDED_DISTANCE,
};

interface ViewAllProps {
	className: string;
	trackViewAllClick: () => void;
	citySearchSlug: string;
	total: number;
}
const ViewAll = ({
	className,
	trackViewAllClick,
	citySearchSlug,
	total,
}: ViewAllProps) => {
	return (
		<div className={className}>
			<ViewAllButton
				className={classNames(Styles.viewAllButton, Styles.viewAllButtonWide)}
				color={'secondary'}
				onClick={trackViewAllClick}
				slug={citySearchSlug}
				text={renderViewAllText(total, true)}
			/>
		</div>
	);
};

/** @internal */
export const CityRecommendations: FunctionComponent<Props> = ({
	category,
	city,
	isPermitted,
	stateCode,
	total,
	vendors,
}) => {
	const { track } = useAnalyticsContext();
	const isDirectoryPageMinPeakPrice = useIsDirectoryPageMinPeakPriceEnabled();

	const trackCardClick = () => {
		track({
			event: CLICK_THROUGH_TO_VENDORS,
			properties: {
				...sourceInfo,
				vendorCategoryCode: category.code,
			},
		});
	};

	const trackViewAllClick = () =>
		track({
			event: CLICK_THROUGH_TO_VENDOR_CATEGORY,
			properties: {
				sourcePage: STATE_LANDING_PAGE,
				sourceContent: RECOMMENDED_VENDORS,
				vendorCategoryCode: category.code,
			},
		});

	const citySearchSlug = `/marketplace/${urlSafe(
		`${category.plural?.slug || ''}-${city}-${stateCode}`,
	)}?${INTERNAL_REFERRER_SORT_PARAM}`;

	if (vendors.length < ADDITIONAL_CITY_VENDOR_LIMIT || !isPermitted)
		return null;

	const slicedProfiles = vendors.slice(0, ADDITIONAL_CITY_VENDOR_LIMIT);

	// Prepare impressions
	const impressionType: ImpressionType = `Landings: State Small ${city}`;
	const trackImpression = useTrackVendorImpression({
		impressionType,
	});

	return (
		<GridColumn
			md="6"
			key={city}
			className={classNames(Styles.container, Styles.containerWide)}
		>
			<div className={Styles.header}>
				<a className={StateStyles.clickableHeader} href={citySearchSlug}>
					{
						<TextEditorial bold data-testid="additional-cities-header">
							{`Near ${startCase(city)}, ${stateCode}`}
						</TextEditorial>
					}
				</a>
				<ViewAll
					className={ClientStyles.hideInlineOnMobile}
					trackViewAllClick={trackViewAllClick}
					citySearchSlug={citySearchSlug}
					total={total}
				/>
			</div>

			<TrackableContainer
				trackingData={widgetViewed(STATE_LANDING_PAGE, category.code)}
			>
				<GridRow>
					{slicedProfiles.map((vendor, i) => {
						const impressionDetails: VendorImpressions.ImpressionDetails = {
							city,
							impressionType,
							locationIndex: i + 1,
							vendor,
						};

						const vendorImpressionCallback = () => {
							trackImpression(impressionDetails);
						};

						const [cardRef] = useIsVisible<HTMLDivElement>({
							callback: vendorImpressionCallback,
						});

						return (
							<GridColumn
								lg="6"
								className={classNames(
									Styles.vendorCardWrapper,
									Styles.vendorCardWrapperWide,
								)}
								key={vendor.id}
							>
								<div ref={cardRef}>
									{
										<OdsVendorCard
											{...OrionVendorCardProps(
												vendor,
												true,
												true,
												true,
												isDirectoryPageMinPeakPrice,
											)}
											size="sm"
											id={vendor.id}
											onClick={trackCardClick}
											vendorRaw={vendor}
										/>
									}
								</div>
							</GridColumn>
						);
					})}
				</GridRow>
			</TrackableContainer>

			<ViewAll
				className={ClientStyles.hideOnDesktop}
				trackViewAllClick={trackViewAllClick}
				citySearchSlug={citySearchSlug}
				total={total}
			/>
		</GridColumn>
	);
};

type CityParam = { city: string };

const mapStateToProps = (
	state: Redux.State,
	{ city }: CityParam,
): StateProps => ({
	category: getCategory(state),
	stateCode: getStateCode(state),
	total: getRecommendedVendorTotalByCity(state, city),
	vendors: getRecommendedVendorProfilesByCity(state, city),
	isPermitted: isRecommendationPermittedByCity(city, state),
});

export default connect(mapStateToProps)(CityRecommendations);
